import type {
  ApiRequestHeaders,
  ApiRequestMethod,
  ApiRequestOptions,
  ApiResponseState,
} from './client.types'
import { xhrRequest } from './clientXhr'
import { REQUEST_TIMEOUT_MS } from './config'
import { headersBase } from './headers'
import { ResponseStatusCodes } from './responseStatusCodes'
import { toResponseError } from './toResponseError'

/** The timeout covers the body as well as the headers, so a body that never ends cannot hang the request. */
export async function apiRequest<T, B = unknown>(
  requestURL: URL,
  method: ApiRequestMethod,
  body: B,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
  timeout: number = REQUEST_TIMEOUT_MS,
): Promise<ApiResponseState<T>> {
  if (options?.onUploadProgress) {
    // deliberately not given `timeout`: uploads run on UPLOAD_TIMEOUT_MS, not the request timeout
    return xhrRequest<T, B>(requestURL, method, body, headers, options)
  }

  const requestParameters: RequestInit = {
    method,
    headers: headersBase(!!options?.webview, headers),
    keepalive: options?.keepalive,
    credentials: options?.credentials,
  }

  if (method !== 'GET' && method !== 'HEAD') {
    requestParameters.body = body instanceof FormData ? body : JSON.stringify(body)
  }

  const state: ApiResponseState<T> = {
    isOk: false,
    isError: false,
    isAuthError: false,
    isInvalid: false,
    data: undefined,
    error: undefined,
    responseStatus: undefined,
  }

  const controller = new AbortController()
  const timeoutID = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(requestURL.href, {
      ...requestParameters,
      signal: controller.signal,
    })
    state.responseStatus = response.status
    if (!response.ok) {
      const errorBody = await response.json().catch(() => undefined)
      throw toResponseError(errorBody, response.status, response.statusText)
    }
    const result = await response.json()
    if (result.error) {
      state.isInvalid = true
      throw result.error
    }
    state.isOk = true
    state.data = result
  } catch (error) {
    const responseError = error as NonNullable<ApiResponseState<T>['error']>
    state.error = responseError
    state.isError = true
    state.isAuthError =
      state.responseStatus === ResponseStatusCodes.UNAUTHORIZED ||
      responseError?.code === ResponseStatusCodes.UNAUTHORIZED
  } finally {
    clearTimeout(timeoutID)
  }

  return Object.freeze(state)
}
